'use client'

import { memo } from 'react'
import { X } from 'lucide-react'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import BookingSummaryFooter from '../../../ui/booking-summary-footer'
import type { RoomBooking } from '~/types/booking'
import { useBookingCart } from '~/stores/booking-cart'
import SwipeableRoomItem from './swipeable-room-item'
import {
  calculateDurationOfStay,
  formatDate,
  formatDateShort,
  formatSingleDate,
} from '~/lib/date-utils'
import { getTranslation } from '~/lib/i18n-utils'
import { useLocale } from '~/contexts/LocaleContext'

interface BookingDetailsPopupProps {
  isOpen: boolean
  onClose: () => void
  checkIn: string
  checkOut: string
  checkInTime: string
  checkOutTime: string
  totalRooms: number
  rooms: RoomBooking[]
  onUpdateGuests: (
    cartItemId: string,
    adults: number,
    children6To12: number,
    childrenUnder6: number
  ) => void
  onContinue: () => void
}

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const modalVariants = {
  hidden: { y: '100%', opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring' as const,
      damping: 25,
      stiffness: 300,
    },
  },
  exit: {
    y: '100%',
    opacity: 0,
    transition: {
      type: 'spring' as const,
      damping: 25,
      stiffness: 300,
    },
  },
}

function BookingDetailsPopup({
  isOpen,
  onClose,
  checkIn,
  checkInTime,
  checkOut,
  checkOutTime,
  totalRooms,
  rooms,
  onUpdateGuests,
  onContinue,
}: BookingDetailsPopupProps) {
  const [editingRoomId, setEditingRoomId] = useState<string | null>(null)
  const [footerHeight, setFooterHeight] = useState(80)
  const { removeFromCart } = useBookingCart()
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const { totalAmount } = useBookingCart((state) => ({
    totalAmount: state.totalAmount,
  }))
  const handleGuestChange = (
    cartItemId: string,
    type: 'adults' | 'children6To12' | 'childrenUnder6',
    value: number
  ) => {
    const room = rooms.find((r) => r.cartItemId === cartItemId)
    if (!room) return

    onUpdateGuests(
      cartItemId,
      type === 'adults' ? value : room.adults,
      type === 'children6To12' ? value : room.children6To12,
      type === 'childrenUnder6' ? value : room.childrenUnder6
    )
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/50"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={overlayVariants}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto bg-white"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-4">
                <div>
                  <h3 className="font-lora text-[18px] font-medium text-secondary-950">
                    {t('booking.detailsTitle')}
                  </h3>
                  <p className="mt-1 font-lexend text-xs font-[350] text-[#666B74]">
                    {totalRooms} {t('booking.rooms')}, {formatDate(checkIn)} -{' '}
                    {formatDate(checkOut)}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 transition-colors hover:text-gray-500"
                >
                  <X className="h-6 w-6 text-secondary-950" />
                </button>
              </div>

              <div className="p-4">
                {/* Thông tin ngày đặt phòng */}
                <div className="rounded-[6px] bg-[#F3F3F3] p-3">
                  <div className="flex items-center justify-between text-[12px] text-[#2D2E32]">
                    <div className="flex flex-col">
                      <label>{t('booking.checkIn')}</label>
                      <span className="font-medium">
                        {formatDateShort(new Date(checkIn), locale, t)}
                      </span>
                      <span>
                        {t('booking.from')} {checkInTime}
                      </span>
                    </div>
                    <div className="h-[1px] w-[9%] bg-[#3A3B3F]"></div>
                    <div className="flex flex-col text-right">
                      <label>{t('booking.checkOut')}</label>
                      <span className="font-medium">
                        {formatDateShort(new Date(checkOut), locale, t)}
                      </span>
                      <span>
                        {t('booking.before')} {checkOutTime}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between border-t border-[#CED0D3] pt-3 text-[12px] text-[#2D2E32]">
                    <div className="flex flex-1 flex-col items-start">
                      <label>{t('booking.totalStay')}</label>
                      <span className="font-medium">
                        {String(calculateDurationOfStay(checkIn, checkOut)).padStart(2, '0')}{' '}
                        {t('booking.days')}
                      </span>
                    </div>
                    <div className="h-[21px] w-[1px] bg-[#CED0D3]"></div>
                    <div className="flex flex-1 flex-col items-end text-left">
                      <div className="flex flex-col self-end text-left">
                        <label>{t('booking.totalRooms')}</label>
                        <span className="text-right font-medium">
                          {String(totalRooms).padStart(2, '0')} {t('booking.rooms')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4" style={{ marginBottom: footerHeight }}>
                  {rooms.map((room, index) => (
                    <div
                      key={room.cartItemId}
                      id={`room-card-${room.cartItemId}`}
                      className="relative"
                    >
                      <SwipeableRoomItem
                        room={room}
                        roomNumber={index + 1}
                        onDelete={removeFromCart}
                        editingRoomId={editingRoomId}
                        setEditingRoomId={setEditingRoomId}
                        handleGuestChange={handleGuestChange}
                      />
                      {index < rooms.length - 1 && (
                        <div className="my-4 h-[1px] w-full bg-[#F3F3F3]"></div>
                      )}
                    </div>
                  ))}
                </div>

                <BookingSummaryFooter
                  onContinue={onContinue}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  totalRooms={totalRooms}
                  rooms={rooms}
                  checkInTime={checkInTime}
                  checkOutTime={checkOutTime}
                  totalAmount={totalAmount}
                  isDetail
                  onHeightChange={setFooterHeight}
                />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

function arePropsEqual(prevProps: BookingDetailsPopupProps, nextProps: BookingDetailsPopupProps) {
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.checkIn === nextProps.checkIn &&
    prevProps.checkOut === nextProps.checkOut &&
    prevProps.checkInTime === nextProps.checkInTime &&
    prevProps.checkOutTime === nextProps.checkOutTime &&
    prevProps.totalRooms === nextProps.totalRooms &&
    prevProps.rooms.length === nextProps.rooms.length &&
    prevProps.rooms.every((room, index) => {
      const nextRoom = nextProps.rooms[index]
      return (
        room.cartItemId === nextRoom.cartItemId &&
        room.price === nextRoom.price &&
        room.adults === nextRoom.adults &&
        room.children6To12 === nextRoom.children6To12 &&
        room.childrenUnder6 === nextRoom.childrenUnder6
      )
    })
  )
}

export default memo(BookingDetailsPopup, arePropsEqual)
